import { Box, HBox, VBox } from "@ui/box/Box"
import { getIssuerDisplayName } from "../issuer/issuerIdentity"
import Button from "@ui/button/Button"
import { Typo } from "@ui/text/Typo"
import { PositiveSizeChart } from "@ui/chart/PositiveSizeChart"
import Input from "@ui/input/Input"
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react"
import { formatCardNumber } from "src/tools/tools"
import { GD } from "src/GD" // Import GD
import { MastercardIcon } from "src/assets/MastercardIcon"
import { VisaIcon } from "src/assets/VisaIcon"
import { FORM_ACTIONS_SX, FormParser } from "src/view/FormParser"
import { useRouter } from "src/services/Router"
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter"
import { toast } from "react-toastify"
import { Skeleton } from "@ui/skeleton/Skeleton"
import { Spinner } from "@ui/spinner/Spinner"
import { DEFAULT_CURRENCY, MAX_USER_DETAILS_WIDTH } from "src/constants"
import { getFriendlyErrorMessage } from "src/utils/errors"
import {
	AreaChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Tooltip,
	Area
} from "recharts"
import Decimal from "decimal.js"
import { USER_ROLE } from "src/services/AuthService"
import { isCardCreating, isCardPermanentlyBlocked } from "src/utils/cardStatus"
import { createCardBlockMonitor } from "src/utils/cardBlockMonitor"
import { cardBlockScope, forgetCardBlock, getRememberedCardBlock, rememberCardBlock } from "src/services/cardBlockSessions"
import { getNewWithdrawalOutcome } from "src/utils/withdrawals"
import { amountValidationError, maxTopUpMinorUnits, parsePositiveAmountToMinorUnits } from "src/utils/amounts"
import { CardEditForms } from "./CardEditForms"
import { Card3dsCodes } from "./Card3dsCodes"
import { S_UNIVERSAL_DIALOG } from "./universalDialog/signals"
import { UserDetails } from "./UserDetails"
import { useOptionalIssuer } from "../issuer/IssuerProvider"
import { useIsPrivateAccount } from "../private/PrivateAccountContext"
import { useWindowSize } from "@ui/utils/useResize"
import { isMobileViewport } from "@ui/utils/mobileViewport"
import { confirmAction } from "../popup/general/ConfirmDialog"
import { ActionButton, ControlButton, DS, DetailRow, EntityHeader, QuickAction as ToolTile, QuickActions, Section, SettingRow as SecurityRow, Stage, StageButton as HeroActionButton, StageStat as StageLimit } from "../ds/Kit"
import { CardGlyph, CardStatusPill, CardSubView, formatCardFaceNumber } from "./CardsList"

interface CardDetailsProps {
	card: ICardSaved
	onClose: () => void
	onBack?: () => void
	onTransactions?: (cardUid: string) => void
	showEditCard?: boolean
	initialView?: "details" | "edit"
}

const CARD_WITHDRAW_POLL_INTERVAL_MS = 10000
const CARD_TOPUP_POLL_INTERVAL_MS = 2000
const CARD_OPERATION_MAX_POLL_ATTEMPTS = 12
const CARD_TOPUP_MAX_POLL_ATTEMPTS = 10
const CARD_WITHDRAW_BALANCE_MAX_POLL_ATTEMPTS = 15
const CARD_MINOR_UNITS = 100
const CARD_AMOUNT_DECIMALS = 2
const FALLBACK_MIN_RESERVE_CENTS = 500

const formatCardLimit = (limit: number | null | undefined, currency: string | null) =>
	limit === null || limit === undefined || limit === 0
		? "No limits"
		: formatCurrencyWithNumberToSymbolWithNumber(limit, currency || DEFAULT_CURRENCY)

const getCardBalanceMinorUnits = (card: ICardSaved): number | null | undefined =>
	card.providerData?.balance ?? card.balance

const getMaxWithdrawableCents = (card: ICardSaved) =>
	Math.max(0, (getCardBalanceMinorUnits(card) ?? 0) - (card.withdraw_residual_cents ?? FALLBACK_MIN_RESERVE_CENTS))

const formatCardAmountInput = (amountInCents: number) =>
	new Decimal(amountInCents)
		.div(CARD_MINOR_UNITS)
		.toFixed(CARD_AMOUNT_DECIMALS)

const CARD_FREEZE_REASON_LABELS: Record<string, string> = {
	manual: "Manually frozen",
	daily_limit: "Auto-frozen: daily limit reached",
	monthly_limit: "Auto-frozen: monthly limit reached",
	suspicious: "Auto-frozen: suspicious transactions"
}

const TERMINAL_CARD_STATUSES = new Set([
	"blocked",
	"closed",
	"lost",
	"replaced",
	"unavailable",
	"declined",
	"error"
])
type TCardPendingAction = "freeze" | "unfreeze" | "block" | null
type TCardOperationPhase = "pending" | "success" | "warning" | "error"

interface ICardOperationNotice {
	phase: TCardOperationPhase
	message: string
}

const CARD_OPERATION_NOTICE_COLORS: Record<
	TCardOperationPhase,
	{ background: string; border: string; text: string }
> = {
	pending: { background: "#EFF6FF", border: "#93C5FD", text: "#1D4ED8" },
	success: { background: "#ECFDF5", border: "#86EFAC", text: "#047857" },
	warning: { background: "#FFFBEB", border: "#FCD34D", text: "#B45309" },
	error: { background: "#FEF2F2", border: "#FCA5A5", text: "#B91C1C" }
}

const BalanceLoadingIndicator = ({ label = "Updating card balance", light = false }: { label?: string; light?: boolean }) => (
	<HBox
		role='status'
		aria-label={label}
		gap='8px'
		sx={{ minHeight: "28px", alignItems: "center", color: light ? "#FFFFFF" : "#475569" }}
	>
		<Spinner size={20} color={light ? "#FFFFFF" : "#2563EB"} />
		<Typo sx={{ color: light ? "rgba(255,255,255,0.85)" : "#64748B", fontSize: "0.75rem", fontWeight: 700 }}>
			Updating…
		</Typo>
	</HBox>
)

/** The card balance and its actions. It sits on the card stage next to the card, so it has no box of its own. */
const BalanceHero = ({ loading, amount, hint, actions, footer }: { loading: boolean; amount: string; hint?: ReactNode; actions?: ReactNode; footer?: ReactNode }) => (
	<VBox gap='18px' sx={{ justifyContent: "center", minWidth: 0, padding: "4px 4px 4px 8px", [DS.mobile]: { padding: 0 } }}>
		<VBox gap='6px'>
			<Typo sx={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: DS.muted }}>Available on card</Typo>
			{loading ? (
				<BalanceLoadingIndicator />
			) : (
				<Typo sx={{ fontSize: "44px", lineHeight: 1.05, fontWeight: 700, letterSpacing: "-0.035em", color: DS.ink, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere", [DS.mobile]: { fontSize: "36px" } }}>{amount}</Typo>
			)}
			{hint && <Typo sx={{ fontSize: "13px", color: DS.muted }}>{hint}</Typo>}
		</VBox>
		{actions && <HBox gap='10px' sx={{ flexWrap: "wrap" }}>{actions}</HBox>}
		{footer}
	</VBox>
)

/** A limit shown as a value with a quiet pencil; the whole value is the edit button. */
const LimitEditButton = ({ label, disabled, onClick, children }: { label: string; disabled: boolean; onClick: () => void; children: ReactNode }) => (
	<Box
		element='button'
		type='button'
		aria-label={label}
		disabled={disabled}
		onClick={onClick}
		sx={{
			display: "inline-flex",
			alignItems: "center",
			gap: "6px",
			padding: 0,
			border: 0,
			background: "transparent",
			font: "inherit",
			letterSpacing: "inherit",
			color: "inherit",
			textAlign: "left",
			cursor: disabled ? "default" : "pointer",
			"& .edit-icon": { color: DS.faint },
			"&:hover:not(:disabled) .edit-icon": { color: DS.accent },
			"&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "2px", borderRadius: "4px" }
		}}
	>
		{children}
		<Box element='span' className='edit-icon' aria-hidden sx={{ display: disabled ? "none" : "inline-flex" }}>
			<svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
				<path d='M12 20h9' /><path d='M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z' />
			</svg>
		</Box>
	</Box>
)

export const CardDetails = ({
	card: initialCard,
	onClose,
	onBack,
	onTransactions,
	showEditCard = true,
	initialView = "details"
}: CardDetailsProps) => {
	const [cardDetails, updateCardDetails, setCardDetails, cardDetailsBusy] = GD.REQ_GET_CARD.useRequest()
	const [users, updateUsers] = GD.REQ_USERS.useRequest()
	const [company, updateCompany, , companyBusy] = GD.REQ_COMPANY_GET.useRequest()
	const isPrivateAccount = useIsPrivateAccount()
	const [trxResponse, setTrxResponse] =
		useState<IAPIResponse<ICompanyTransactionsResponse>>()
	const [showNumber, setShowNumber] = useState(false)
	const [showExpiry, setShowExpiry] = useState(false)
	const [showCvv, setShowCvv] = useState(false)
	const [activeView, setActiveView] = useState<"details" | "edit" | "topup" | "refund" | "tx-control" | "3ds">(
		initialView
	)
	const [refundAmount, setRefundAmount] = useState("")
	const [topUpAmount, setTopUpAmount] = useState(0)
	const [topUpFee, setTopUpFee] = useState<{ percent: number; fixed: number } | null>(null)
	// Sub-views replace the card in place, so hand the dialog a back action instead
	// of leaving the user with only "close the whole sheet".
	useEffect(() => {
		S_UNIVERSAL_DIALOG.setBackHandler(
			activeView === "details" ? undefined : () => setActiveView("details")
		)
		return () => S_UNIVERSAL_DIALOG.setBackHandler(undefined)
	}, [activeView])
	const [actionBusy, setActionBusy] = useState(false)
	const [statusRefreshBusy, setStatusRefreshBusy] = useState(false)
	const [withdrawBusy, setWithdrawBusy] = useState(false)
	const [cardBalanceSyncing, setCardBalanceSyncing] = useState(false)
	const [pendingAction, setPendingAction] = useState<TCardPendingAction>(null)
	const [showBlockConfirmation, setShowBlockConfirmation] = useState(false)
	const [blockPreview, setBlockPreview] = useState<ICardBlockPreview | null>(null)
	const [blockPreviewLoading, setBlockPreviewLoading] = useState(false)
	const [blockPreviewError, setBlockPreviewError] = useState<string | null>(null)
	const [cardOperationNotice, setCardOperationNotice] =
		useState<ICardOperationNotice | null>(null)
	const [resolvedCardUid, setResolvedCardUid] = useState<string | null>(null)
	const pollTimers = useRef(new Set<number>())
	const disposed = useRef(false)
	const blockMonitor = useRef<ReturnType<typeof createCardBlockMonitor> | null>(null)
	const blockToastId = useRef<string | null>(null)
	const confirmedBlockedCard = useRef<{ uid: string; status: ICardSaved["status"] } | null>(
		isCardPermanentlyBlocked(initialCard.status) ? { uid: initialCard.uid, status: initialCard.status } : null
	)
	const visibleCardUid = useRef(initialCard.uid)
	visibleCardUid.current = initialCard.uid
	const [profile, , setProfile, profileBusy] = GD.REQ_PROFILE.useRequest()
	const issuerContext = useOptionalIssuer()
	const { width: viewportWidth, height: viewportHeight } = useWindowSize()
	const isMobile = isMobileViewport(viewportWidth, viewportHeight)
	const setIssuerBalanceSyncing = issuerContext?.setBalanceSyncing
	const router = useRouter()
	const setBalanceSyncing = (syncing: boolean) => {
		setCardBalanceSyncing(syncing)
		setIssuerBalanceSyncing?.(syncing)
	}

	useEffect(() => {
		const timers = pollTimers.current
		disposed.current = false
		return () => {
			disposed.current = true
			timers.forEach(timer => window.clearTimeout(timer))
			timers.clear()
		}
	}, [])

	useEffect(() => () => setIssuerBalanceSyncing?.(false), [setIssuerBalanceSyncing])

	useEffect(() => {
		setActionBusy(false)
		setPendingAction(null)
		setCardOperationNotice(null)
		setShowBlockConfirmation(false)
		return () => {
			blockMonitor.current?.stop()
			blockMonitor.current = null
			if (blockToastId.current) toast.dismiss(blockToastId.current)
		}
	}, [initialCard.uid])

	const schedulePoll = (callback: () => void, delay: number) => {
		if (disposed.current) return
		const timer = window.setTimeout(() => {
			pollTimers.current.delete(timer)
			if (!disposed.current) callback()
		}, delay)
		pollTimers.current.add(timer)
	}

	useEffect(() => {
		let active = true
		const requestedCardUid = initialCard.uid
		void (async () => {
			try {
				const response = await GD.REQ_GET_CARD.request({
					uid: initialCard.uid,
					card_holder_uid: initialCard.card_holder_uid
				})
				if (active) {
					if (response.data?.uid === requestedCardUid && isCardPermanentlyBlocked(response.data.status)) {
						confirmedBlockedCard.current = { uid: requestedCardUid, status: response.data.status }
					}
					setCardDetails(response)
					if (response.data?.uid === requestedCardUid) GD.S_CARD_UPDATED.invoke(response.data)
				}
			} finally {
				if (active) setResolvedCardUid(requestedCardUid)
			}
		})()
		updateUsers()
		if (!isCardCreating(initialCard.status)) {
			GD.REQ_COMPANY_TRANSACTIONS_CARD.request({
				card_uid: initialCard.uid,
				limit: 100,
				page: 0
			}).then(setTrxResponse)
		}
		return () => {
			active = false
		}
	}, [initialCard])

	const currentCardDetails =
		cardDetails?.data?.uid === initialCard.uid ? cardDetails.data : undefined
	const loadedCard = currentCardDetails || initialCard
	const card = confirmedBlockedCard.current?.uid === loadedCard.uid && !isCardPermanentlyBlocked(loadedCard.status)
		? { ...loadedCard, status: confirmedBlockedCard.current.status } : loadedCard
	const topUpCurrency = card.currency || DEFAULT_CURRENCY
	const cardIssuer = Number.isInteger(card.issuer) ? card.issuer : null
	const companyIssuerBalance =
		issuerContext?.issuers.find((account) => account.issuer === cardIssuer)?.balance ??
		company?.data?.balances?.find((account) => account.issuer === cardIssuer)?.balance
	const buyerIssuerBalance = profile?.team_balances?.find(
		(account) => account.issuer === cardIssuer
	)?.team_balance
	const topUpAvailableBalance =
		profile?.company_role === USER_ROLE.BUYER
			? buyerIssuerBalance
			: companyIssuerBalance
	const topUpBalanceLoading =
		profileBusy ||
		(profile?.company_role !== USER_ROLE.BUYER && companyBusy && companyIssuerBalance === undefined)
	const topUpBalanceOwner =
		profile?.company_role === USER_ROLE.BUYER ? "buyer " : isPrivateAccount ? "" : "company "
	const topUpBalanceSource =
		`${cardIssuer === null ? "Card" : getIssuerDisplayName({ issuer: cardIssuer, issuer_label: card.issuer_label })} ${topUpBalanceOwner}balance`
	const maxTopUp = topUpFee && topUpAvailableBalance !== undefined
		? maxTopUpMinorUnits(topUpAvailableBalance, topUpFee.percent, topUpFee.fixed)
		: undefined
	const topUpFeeRule = topUpFee
		? `${(topUpFee.percent / 100).toFixed(2)}%${topUpFee.fixed ? ` + ${formatCurrencyWithNumberToSymbolWithNumber(topUpFee.fixed, topUpCurrency)}` : ""}`
		: ""
	const user = (users?.data as IUserSaved[])?.find((u) => u.uid === card.user_uid)

	// The top-up fee tariff only comes with the card template's bins_info; without it we show the raw balance.
	useEffect(() => {
		if (activeView !== "topup" || cardIssuer === null) return
		let active = true
		void (async () => {
			try {
				const res = await GD.REQ_GET_CARD_TEMPLATE.request({ issuer: cardIssuer })
				const info = res.data?.bins_info?.find(b => b.issuer === cardIssuer)
				if (active) setTopUpFee(info ? { percent: info.topup_commission_percent ?? 0, fixed: info.topup_fixed_fee ?? 0 } : null)
			} catch {
				if (active) setTopUpFee(null)
			}
		})()
		return () => { active = false }
	}, [activeView, cardIssuer])
	const isLoading = !currentCardDetails && (cardDetailsBusy || resolvedCardUid !== initialCard.uid)
	const cardBalanceLoading = isLoading || cardBalanceSyncing || statusRefreshBusy
	const cardBalance = getCardBalanceMinorUnits(card)
	const cardDetailsFailed = !isLoading && !currentCardDetails && Boolean(cardDetails?.error)
	const hasVisibleCardInfo = /^\d{13,19}$/.test((currentCardDetails?.providerData?.pan || "").replace(/\s/g, ""))
	const areActionsReady = !isLoading && !cardDetailsBusy
	const normalizedStatus = (card.status || "").toLowerCase()
	const isCardFrozen = normalizedStatus === "frozen"
	const isCardActive = normalizedStatus === "active"
	const canEditCardStatus = isCardActive || isCardFrozen
	const canEditCard = profile?.company_role === USER_ROLE.ADMIN ||
		profile?.company_role === USER_ROLE.TEAM_LEAD ||
		(profile?.company_role === USER_ROLE.BUYER && Boolean(profile.uid) && card.user_uid === profile.uid)
	const isCreating = isCardCreating(normalizedStatus)
	const isCardTerminal = TERMINAL_CARD_STATUSES.has(normalizedStatus)
	const isFreezePending = pendingAction === "freeze"
	const isUnfreezePending = pendingAction === "unfreeze"
	const isBlockPending = pendingAction === "block"
	const canManageTxControl = profile?.company_role === USER_ROLE.ADMIN
	const canBlockCard = card.can_block !== false
	const canFreezeCard = card.can_freeze !== false
	const cardholderDisplayName = user?.name ||
		(card.user_uid === profile?.uid ? profile?.name : undefined) || "N/A"
	const isSuspiciousFrozenCard = card.frozen_reason === "suspicious"
	const canUnfreezeCard = card.can_unfreeze !== false && (!isSuspiciousFrozenCard || canManageTxControl)
	const freezeReasonLabel = card.frozen_reason
		? (CARD_FREEZE_REASON_LABELS[card.frozen_reason] ?? card.frozen_reason)
		: null
	const buyer = !isPrivateAccount && user?.company_role === USER_ROLE.BUYER ? user : null
	const openBuyerDetails = () => {
		if (!buyer) return
		S_UNIVERSAL_DIALOG.invoke({
			sx: { maxWidth: MAX_USER_DETAILS_WIDTH },
			contentPadding: "0px",
			content: (
				<UserDetails
					userUid={buyer.uid}
					onClose={() => S_UNIVERSAL_DIALOG.invoke(null)}
					onUpdate={async () => {
						await GD.REQ_USERS.request()
					}}
				/>
			),
			closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null)
		})
	}

	useEffect(() => {
		if (user?.company_uid) {
			updateCompany(user.company_uid)
		}
	}, [user])

	const refreshCardState = async () => {
		const nextCardDetails = await GD.REQ_GET_CARD.request({
			uid: card.uid,
			card_holder_uid: card.card_holder_uid
		})
		if (nextCardDetails.error) throw nextCardDetails.error
		if (nextCardDetails.data?.uid !== card.uid) throw new Error("Card status could not be loaded.")
		const freshCard = confirmedBlockedCard.current?.uid === card.uid && !isCardPermanentlyBlocked(nextCardDetails.data.status)
			? { ...nextCardDetails.data, status: confirmedBlockedCard.current.status } : nextCardDetails.data
		if (disposed.current || visibleCardUid.current !== card.uid) return
		if (isCardPermanentlyBlocked(freshCard.status)) {
			confirmedBlockedCard.current = { uid: card.uid, status: freshCard.status }
		}
		setCardDetails({ data: freshCard })
		GD.S_CARD_UPDATED.invoke(freshCard)
		const [cardsResult] = await Promise.allSettled([
			GD.REQ_CARDS.request({ force: true }),
			GD.REQ_COMPANIES.request({ force: true }),
			GD.REQ_PROFILE.request()
		])
		if (cardsResult.status === "fulfilled" && cardsResult.value.data) {
			GD.S_CARDS_LIST_UPDATED.invoke(cardsResult.value.data)
		}
		return freshCard
	}

	const refreshLinkedBalanceViews = async (freshCard: ICardSaved) => {
		GD.S_CARD_UPDATED.invoke(freshCard)
		const [cardsResult, profileResult, issuerResult] = await Promise.allSettled([
			GD.REQ_CARDS.request({ force: true }),
			GD.REQ_PROFILE.request(),
			issuerContext?.refreshIssuers() ?? Promise.resolve(true)
		])
		if (cardsResult.status === "fulfilled" && cardsResult.value.data) {
			GD.S_CARDS_LIST_UPDATED.invoke(cardsResult.value.data)
		}
		if (profileResult.status === "fulfilled" && profileResult.value) {
			setProfile(profileResult.value)
		}

		const issuerBalanceUpdated =
			!issuerContext || (issuerResult.status === "fulfilled" && issuerResult.value)
		const buyerBalanceUpdated =
			profile?.company_role !== USER_ROLE.BUYER ||
			(profileResult.status === "fulfilled" && Boolean(profileResult.value))
		return issuerBalanceUpdated && buyerBalanceUpdated
	}

	const refreshCardBalanceAfterTopUp = async (
		previousBalance: number | null | undefined,
		attempt = 0
	): Promise<void> => {
		if (disposed.current) return

		const response = await GD.REQ_GET_CARD.request({
			uid: card.uid,
			card_holder_uid: card.card_holder_uid
		})
		if (disposed.current) return

		if (!response.error && response.data?.uid) {
			setCardDetails(response)
			if (getCardBalanceMinorUnits(response.data) !== previousBalance) {
				const linkedBalancesUpdated = await refreshLinkedBalanceViews(response.data)
				if (disposed.current) return
				setBalanceSyncing(false)
				if (linkedBalancesUpdated) {
					setCardOperationNotice({ phase: "success", message: "Card topped up successfully." })
					toast.success("Card topped up successfully.")
				} else {
					const message = "Card balance updated, but the available balance could not be refreshed. Retry from the issuer menu."
					setCardOperationNotice({ phase: "warning", message })
					toast.warn(message)
				}
				return
			}
		}

		if (attempt + 1 >= CARD_TOPUP_MAX_POLL_ATTEMPTS) {
			setBalanceSyncing(false)
			const message =
				"Top up completed, but the updated card balance is still syncing. Use Refresh status to check again."
			setCardOperationNotice({ phase: "warning", message })
			toast.warn(message)
			return
		}

		schedulePoll(
			() => void refreshCardBalanceAfterTopUp(previousBalance, attempt + 1),
			CARD_TOPUP_POLL_INTERVAL_MS
		)
	}

	const refreshCardBalanceAfterWithdrawal = async (
		previousBalance: number | null | undefined,
		attempt = 0
	): Promise<void> => {
		if (disposed.current) return

		const response = await GD.REQ_GET_CARD.request({
			uid: card.uid,
			card_holder_uid: card.card_holder_uid
		})
		if (disposed.current) return

		if (!response.error && response.data?.uid) {
			setCardDetails(response)
			if (getCardBalanceMinorUnits(response.data) !== previousBalance) {
				const linkedBalancesUpdated = await refreshLinkedBalanceViews(response.data)
				if (disposed.current) return
				setBalanceSyncing(false)
				if (linkedBalancesUpdated) {
					setCardOperationNotice({ phase: "success", message: "Withdrawal processed. Balances updated." })
					toast.success("Withdrawal processed. Balances updated.")
				} else {
					const message = "Card balance updated, but the available balance could not be refreshed. Retry from the issuer menu."
					setCardOperationNotice({ phase: "warning", message })
					toast.warn(message)
				}
				return
			}
		}

		if (attempt + 1 >= CARD_WITHDRAW_BALANCE_MAX_POLL_ATTEMPTS) {
			setBalanceSyncing(false)
			const message =
				"Withdrawal processed, but the updated card balance is still syncing. Use Refresh status to check again."
			setCardOperationNotice({ phase: "warning", message })
			toast.warn(message)
			return
		}

		schedulePoll(
			() => void refreshCardBalanceAfterWithdrawal(previousBalance, attempt + 1),
			CARD_TOPUP_POLL_INTERVAL_MS
		)
	}

	const handleRefreshCardStatus = async () => {
		setStatusRefreshBusy(true)
		setBalanceSyncing(true)
		try {
			if (blockMonitor.current && !blockMonitor.current.isSettled()) {
				await blockMonitor.current.check()
			} else {
				const refreshed = await refreshCardState()
				if (refreshed && blockMonitor.current?.isCompleted() && isCardPermanentlyBlocked(refreshed.status)) {
					setCardOperationNotice({ phase: "success", message: "Card is blocked. Card status refreshed." })
				}
				toast.success("Card status refreshed.")
			}
		} catch (error) {
			toast.error(getFriendlyErrorMessage(error, "Failed to refresh card status."))
		} finally {
			setStatusRefreshBusy(false)
			setBalanceSyncing(false)
		}
	}

	const requestCardBlock = async () => {
		if (actionBusy || (blockMonitor.current && !blockMonitor.current.isSettled())) return
		setShowBlockConfirmation(false)
		setPendingAction("block")
		setActionBusy(true)
		setCardOperationNotice({ phase: "pending", message: "Starting card block..." })
		let response: IAPIResponse<ICardBlockResponse>
		const scope = cardBlockScope(profile?.uid ?? "")
		try {
			response = await GD.REQ_BLOCK_CARD.request({
				card_uid: card.uid,
				...(profile?.company_role === USER_ROLE.BUYER ? {} : { type: "company" })
			})
		} catch {
			response = { error: { code: -1, message: "Could not confirm whether blocking started. Refresh the card before retrying." } }
		}
		if (!response.error && response.data?.operation_uid) {
			rememberCardBlock(scope, card.uid, response.data.operation_uid)
		}
		if (disposed.current || visibleCardUid.current !== card.uid) return

		if (response.error || !response.data?.operation_uid) {
			setActionBusy(false)
			setPendingAction(null)
			// Wasabi cards do not support the block pipeline (backend returns
			// "...not supported for the active provider, got: wasabi"). Surface a
			// clear, actionable message instead of the raw provider error.
			const blockMessage = response.error?.message || ""
			if (/not supported/i.test(blockMessage)) {
				const message = "Blocking isn't supported for this card's provider. Freeze the card instead."
				setCardOperationNotice({ phase: "error", message })
				toast.error(message)
			} else {
				const message = getFriendlyErrorMessage(response.error, "Failed to start card block.")
				setCardOperationNotice({ phase: "error", message })
				toast.error(message)
			}
			return
		}

		startCardBlockMonitor(response.data.operation_uid)
	}

	const startCardBlockMonitor = useCallback((operationUid: string) => {
		const scope = cardBlockScope(profile?.uid ?? "")
		setPendingAction("block")
		setActionBusy(true)
		const toastId = `card-block-${operationUid}`
		blockToastId.current = toastId
		toast.info("Card block started. Checking status...", {
			toastId,
			autoClose: false,
			closeOnClick: false
		})

		blockMonitor.current?.stop()
		blockMonitor.current = createCardBlockMonitor({
			card,
			operationUid,
			readStatus: () => GD.REQ_BLOCK_CARD_STATUS.request({ operation_uid: operationUid }),
			readCard: () => GD.REQ_GET_CARD.request({ uid: card.uid, card_holder_uid: card.card_holder_uid }),
			onCard: (freshCard) => {
				if (isCardPermanentlyBlocked(freshCard.status)) {
					confirmedBlockedCard.current = { uid: freshCard.uid, status: freshCard.status }
				}
				setCardDetails({ data: freshCard })
				GD.S_CARD_UPDATED.invoke(freshCard)
			},
			onNotice: (notice, settled) => {
				setCardOperationNotice(notice)
				if (toast.isActive(toastId)) {
					toast.update(toastId, {
						render: notice.message,
						type: notice.phase === "pending" ? "info" : notice.phase,
						autoClose: settled ? 6000 : false,
						closeOnClick: settled
					})
				} else if (settled) {
					if (notice.phase === "success") toast.success(notice.message)
					else if (notice.phase === "warning") toast.warn(notice.message)
					else toast.error(notice.message)
				}
			},
			onSettled: () => {
				if (!blockMonitor.current?.isCompleted()) forgetCardBlock(scope, card.uid, operationUid)
				setActionBusy(false)
				setPendingAction(null)
			}
		})
		void blockMonitor.current.check()
	}, [card, profile?.uid, setCardDetails])

	useEffect(() => {
		if (!profile?.uid || !currentCardDetails || blockMonitor.current) return
		const operationUid = getRememberedCardBlock(cardBlockScope(profile.uid), initialCard.uid)
		if (operationUid) startCardBlockMonitor(operationUid)
	}, [currentCardDetails, initialCard.uid, profile?.uid, startCardBlockMonitor])

	const handleFreeze = async () => {
		if (actionBusy || !(await confirmAction({ title: "Freeze card", message: "Payments will be paused until you unfreeze this card.", confirm: "Freeze card" }))) return

		setPendingAction("freeze")
		setActionBusy(true)
		setCardOperationNotice({ phase: "pending", message: "Freezing card..." })
		const response = await GD.REQ_FREEZE_CARD.request({ card_uid: card.uid })
		setActionBusy(false)
		setPendingAction(null)

		if (response.error) {
			const message = getFriendlyErrorMessage(response.error, "Failed to freeze card.")
			setCardOperationNotice({ phase: "error", message })
			toast.error(message)
			return
		}

		try {
			await refreshCardState()
			setCardOperationNotice({ phase: "success", message: "Card frozen successfully." })
			toast.success("Card frozen successfully.")
		} catch {
			const message =
				"Card frozen, but the updated status could not be loaded. Refresh the card manually."
			setCardOperationNotice({ phase: "warning", message })
			toast.warn(message)
		}
	}

	const handleUnfreeze = async () => {
		if (actionBusy || !(await confirmAction({ title: "Unfreeze card", message: "This card will be available for payments again.", confirm: "Unfreeze card" }))) return

		setPendingAction("unfreeze")
		setActionBusy(true)
		setCardOperationNotice({ phase: "pending", message: "Unfreezing card..." })
		const response = await GD.REQ_UNFREEZE_CARD.request({ card_uid: card.uid })
		setActionBusy(false)
		setPendingAction(null)

		if (response.error) {
			const message = getFriendlyErrorMessage(response.error, "Failed to unfreeze card.")
			setCardOperationNotice({ phase: "error", message })
			toast.error(message)
			return
		}

		try {
			await refreshCardState()
			setCardOperationNotice({ phase: "success", message: "Card unfrozen successfully." })
			toast.success("Card unfrozen successfully.")
		} catch {
			const message =
				"Card unfrozen, but the updated status could not be loaded. Refresh the card manually."
			setCardOperationNotice({ phase: "warning", message })
			toast.warn(message)
		}
	}

	const handleBlock = async () => {
		if (!canBlockCard || actionBusy) return
		setBlockPreview(null)
		setBlockPreviewError(null)
		setBlockPreviewLoading(true)
		setShowBlockConfirmation(true)
		try {
			const response = await GD.REQ_BLOCK_CARD_PREVIEW.request({
				card_uid: card.uid,
				...(profile?.company_role === USER_ROLE.BUYER ? {} : { type: "company" })
			})
			if (disposed.current || visibleCardUid.current !== card.uid) return
			if (response.error || !response.data) {
				setBlockPreviewError(getFriendlyErrorMessage(response.error, "Could not load the return estimate."))
				return
			}
			setBlockPreview(response.data)
		} catch {
			if (!disposed.current) setBlockPreviewError("Could not load the return estimate.")
		} finally {
			if (!disposed.current && visibleCardUid.current === card.uid) setBlockPreviewLoading(false)
		}
	}

	const handleTopUp = () => {
		setTopUpAmount(0)
		setActiveView("topup")
	}

	const handleRefund = () => {
		const withdrawable = getMaxWithdrawableCents(card)
		// Nothing to withdraw: leave the field empty so its placeholder shows instead
		// of a literal "0.00" the user would have to clear first.
		setRefundAmount(withdrawable > 0 ? formatCardAmountInput(withdrawable) : "")
		setActiveView("refund")
	}

	const canRefund =
		profile?.company_role === "admin" ||
		profile?.company_role === "teamlead" ||
		profile?.company_role === "buyer"

	const handleCardSaved = async (changes: Partial<ICardSaved>) => {
		const refreshed = await GD.REQ_GET_CARD.request({ uid: card.uid, card_holder_uid: card.card_holder_uid })
		const updatedCard = refreshed.data || { ...card, ...changes }
		setCardDetails({ data: updatedCard })
		GD.S_CARD_UPDATED.invoke(updatedCard)
	}

	const handleEdit = () => {
		if (!canEditCard) return
		if (S_UNIVERSAL_DIALOG.hasActiveDialog()) {
			S_UNIVERSAL_DIALOG.invoke({
				label: "Card Details",
				size: "large",
				content: <CardEditForms card={card} onSaved={handleCardSaved} />,
				closeDialog: onClose
			})
			return
		}
		setActiveView("edit")
	}

	const handleTxControl = () => {
		setActiveView("tx-control")
	}

	const handleOpen3ds = () => {
		if (S_UNIVERSAL_DIALOG.hasActiveDialog()) {
			S_UNIVERSAL_DIALOG.invoke({
				label: "Card Details",
				size: "large",
				content: <Card3dsCodes cardUid={card.uid} card={card} />,
				closeDialog: onClose
			})
			return
		}
		setActiveView("3ds")
	}

	const numberText = [card.providerData?.pan, card.external_number]
		.map(value => (value || "").replace(/\s/g, ""))
		.find(value => /^\d{13,19}$/.test(value)) || ""
	const maskedNumber = numberText
		? formatCardNumber(numberText)
		: card.mask || /[*•]/.test(card.external_number || "") ? formatCardFaceNumber(card.mask || card.external_number) : "Number unavailable"
	const cardUid = initialCard.uid

	const chartData = (trxResponse?.data?.transactions || [])
		.filter((t) => t.direction === "expense" && t.status === "completed")
		.reverse()
		.map((t) => {
			const d = new Date(t.created_at)
			return {
				date: `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}`,
				time: `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`,
				amount: t.amount,
				fullDate: d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
			}
		})

	const CardVisual = () => {
		const EyeIcon = () => (
			<svg
				xmlns='http://www.w3.org/2000/svg'
				width='13'
				height='13'
				viewBox='0 0 24 24'
				fill='none'
				stroke='currentColor'
				strokeWidth='2'
				strokeLinecap='round'
				strokeLinejoin='round'
			>
				<path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
				<circle cx='12' cy='12' r='3' />
			</svg>
		)
		const EyeOffIcon = () => (
			<svg
				xmlns='http://www.w3.org/2000/svg'
				width='13'
				height='13'
				viewBox='0 0 24 24'
				fill='none'
				stroke='currentColor'
				strokeWidth='2'
				strokeLinecap='round'
				strokeLinejoin='round'
			>
				<path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24' />
				<line x1='1' y1='1' x2='23' y2='23' />
			</svg>
		)
		const CopyIcon = () => (
			<svg
				xmlns='http://www.w3.org/2000/svg'
				width='11'
				height='11'
				viewBox='0 0 24 24'
				fill='none'
				stroke='currentColor'
				strokeWidth='2'
				strokeLinecap='round'
				strokeLinejoin='round'
			>
				<rect x='9' y='9' width='13' height='13' rx='2' ry='2' />
				<path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' />
			</svg>
		)

		const expiry = `${card.providerData?.expiryMonth?.toString().padStart(2, "0") || ""}/${card.providerData?.expiryYear?.toString().slice(-2) || ""}`
		const controlSx = { border: 0, padding: 0, width: "28px", height: "26px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "5px", background: "rgba(255,255,255,.07)", color: "#CDD0DF", cursor: "pointer", flexShrink: 0, "&:hover:not(:disabled)": { background: "rgba(255,255,255,.16)", color: "#FFFFFF" }, "&:disabled": { opacity: .3, cursor: "default" }, "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: "2px" } }
		const copy = (value: string, message: string) => {
			navigator.clipboard.writeText(value)
			toast.success(message)
		}
		const labelSx = {
			fontSize: "9px",
			lineHeight: 1.3,
			letterSpacing: "0.1em",
			color: "rgba(214, 214, 240, 0.7)",
			textTransform: "uppercase" as const,
			fontWeight: 600
		}
		return <Box data-card-visual sx={{
  width: "100%", aspectRatio: "1.586", boxSizing: "border-box", padding: "18px",
  display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0,
  position: "relative", overflow: "hidden", containerType: "inline-size", borderRadius: "18px",
  background: `radial-gradient(120% 90% at 100% 0%, var(--ds-card-glow, rgba(125, 170, 214,.38)) 0%, transparent 55%), radial-gradient(90% 80% at 0% 100%, rgba(244, 190, 160, .16) 0%, transparent 60%), ${DS.cardFace}`,
  border: "1px solid rgba(255,255,255,.14)", color: "#FFFFFF",
  boxShadow: "0 28px 50px -22px rgba(14, 24, 38,.65), 0 2px 6px rgba(15,14,28,.25)",
  "&::before": { content: '""', position: "absolute", inset: 0, background: "linear-gradient(115deg, transparent 30%, rgba(255,255,255,.07) 45%, transparent 60%)", pointerEvents: "none" },
  "& > *": { position: "relative" },
  "@media (max-width: 480px)": { padding: "14px" }
}}>
  <HBox sx={{ alignItems: "center", justifyContent: "space-between" }}>
    <Typo sx={{ fontSize: "12px", fontWeight: 600, color: "#E4E5EF", letterSpacing: "-.01em" }}>Cardpartner</Typo>
    <Box sx={{ "& svg": { height: "20px", width: "auto" }, "& #Visa_2": { fill: "#FFFFFF" } }}>
      {card.network === 1 ? <VisaIcon /> : card.network === 2 || card.network === 3 ? <MastercardIcon /> : null}
    </Box>
  </HBox>
  <VBox gap="2px" sx={{ minWidth: 0 }}>
    <Box aria-hidden sx={{ width: "38px", height: "28px", borderRadius: "6px", marginBottom: "10px", boxShadow: "inset 0 0 0 1px rgba(120,90,20,.35)", backgroundImage: "linear-gradient(90deg, transparent 32%, rgba(120,90,20,.35) 33%, transparent 34%, transparent 66%, rgba(120,90,20,.35) 67%, transparent 68%), linear-gradient(135deg, #F6E3A1 0%, #D4AF37 45%, #B8912A 100%)" }} />
    <HBox sx={{ alignItems: "center", justifyContent: "space-between" }}>
      <Typo sx={labelSx}>Card number</Typo>
      {!isLoading && <HBox gap="3px">
        <Box element="button" type="button" aria-label={showNumber ? "Hide Number" : "Show Number"} aria-pressed={showNumber} disabled={!numberText} onClick={() => setShowNumber(!showNumber)} sx={controlSx}>{showNumber ? <EyeOffIcon /> : <EyeIcon />}</Box>
        <Box element="button" type="button" aria-label="Copy Full Number" disabled={!numberText} onClick={() => copy(numberText, "Card Number copied successfully")} sx={controlSx}><CopyIcon /></Box>
      </HBox>}
    </HBox>
    {isLoading ? <Skeleton busy width="100%" height="22px" /> : <Typo sx={{
      fontFamily: "'Courier New', monospace", color: "#FFFFFF", fontSize: numberText.length > 16 ? "clamp(12px, 5.3cqi, 18px)" : "clamp(13px, 6cqi, 20px)",
      lineHeight: 1.3, fontWeight: 600, letterSpacing: ".4px", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", userSelect: "all"
    }}>{showNumber ? numberText.match(/.{1,4}/g)?.join(" ") || numberText : maskedNumber}</Typo>}
  </VBox>
  <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto", gap: "8px", alignItems: "end" }}>
    <VBox gap="2px" sx={{ minWidth: 0 }}>
      <Typo sx={{ ...labelSx, lineHeight: "24px" }}>Cardholder</Typo>
      {isLoading ? <Skeleton busy width="80px" height="18px" /> : <HBox gap="2px" sx={{ alignItems: "center", minWidth: 0 }}>
        <Box element={buyer ? "button" : "span"} type={buyer ? "button" : undefined} aria-label={buyer ? `Open buyer ${buyer.name}` : undefined} onClick={buyer ? openBuyerDetails : undefined} sx={{
          border: 0, padding: 0, background: "none", color: "#F1F2F7", fontFamily: "inherit", fontSize: "10px", lineHeight: "24px", fontWeight: 600, textTransform: "uppercase",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "left", cursor: buyer ? "pointer" : "default", "&:focus-visible": { outline: "2px solid #FFFFFF" }
        }}>{cardholderDisplayName}</Box>
        <Box element="button" type="button" aria-label="Copy Cardholder Name" onClick={() => copy(cardholderDisplayName, "Cardholder name copied")} sx={{ ...controlSx, width: "22px", height: "24px", flexShrink: 0 }}><CopyIcon /></Box>
      </HBox>}
    </VBox>
    {card.providerData && (isLoading || hasVisibleCardInfo) && <>
      <VBox gap="2px">
        <HBox gap="2px" sx={{ alignItems: "center" }}><Typo sx={labelSx}>Expires</Typo>{!isLoading && <Box element="button" type="button" aria-label={showExpiry ? "Hide Expiry" : "Show Expiry"} aria-pressed={showExpiry} onClick={() => setShowExpiry(!showExpiry)} sx={{ ...controlSx, width: "22px", height: "24px" }}>{showExpiry ? <EyeOffIcon /> : <EyeIcon />}</Box>}</HBox>
        {isLoading ? <Skeleton busy width="36px" height="18px" /> : <HBox gap="2px" sx={{ alignItems: "center", minHeight: "24px" }}><Typo sx={{ color: "#F1F2F7", fontSize: "11px", fontVariantNumeric: "tabular-nums" }}>{showExpiry ? expiry : "**/**"}</Typo>{showExpiry && <Box element="button" type="button" aria-label="Copy Expiry" onClick={() => copy(expiry, "Expiry copied")} sx={{ ...controlSx, width: "22px", height: "24px" }}><CopyIcon /></Box>}</HBox>}
      </VBox>
      <VBox gap="2px">
        <HBox gap="2px" sx={{ alignItems: "center" }}><Typo sx={labelSx}>CVV</Typo>{!isLoading && <Box element="button" type="button" aria-label={showCvv ? "Hide CVV" : "Show CVV"} aria-pressed={showCvv} onClick={() => setShowCvv(!showCvv)} sx={{ ...controlSx, width: "22px", height: "24px" }}>{showCvv ? <EyeOffIcon /> : <EyeIcon />}</Box>}</HBox>
        {isLoading ? <Skeleton busy width="24px" height="18px" /> : <HBox gap="2px" sx={{ alignItems: "center", minHeight: "24px" }}><Typo sx={{ color: "#F1F2F7", fontSize: "11px" }}>{showCvv ? card.providerData.cvv : "***"}</Typo>{showCvv && <Box element="button" type="button" aria-label="Copy CVV" onClick={() => copy(card.providerData?.cvv || "", "CVV copied")} sx={{ ...controlSx, width: "22px", height: "24px" }}><CopyIcon /></Box>}</HBox>}
      </VBox>
    </>}
  </Box>
</Box>
	}

	if (activeView === "topup") {
		return (
			<Box data-dialog-layout="amount-form" sx={{ minWidth: 0 }}>
				<CardSubView
					title='Top up card'
					description='Add funds from your available balance to this card.'
					card={card}
					masked={maskedNumber}
					stats={[
						{
							label: maxTopUp === undefined ? "Available to top up" : "You can add up to",
							value: topUpBalanceLoading ? (
								<BalanceLoadingIndicator label='Updating available balance' />
							) : topUpAvailableBalance === undefined ? "Unavailable" : formatCurrencyWithNumberToSymbolWithNumber(maxTopUp ?? topUpAvailableBalance, topUpCurrency),
							hint: maxTopUp === undefined || topUpAvailableBalance === undefined
								? topUpBalanceSource
								: `${formatCurrencyWithNumberToSymbolWithNumber(topUpAvailableBalance, topUpCurrency)} ${topUpBalanceSource} minus the ${topUpFeeRule} top-up fee`
						},
						{
							label: "Current card balance",
							value: cardBalanceLoading ? (
								<BalanceLoadingIndicator />
							) : formatCurrencyWithNumberToSymbolWithNumber(getCardBalanceMinorUnits(card) || 0, topUpCurrency)
						}
					]}
				>
					<Section title='Amount to add'>
					<FormParser
						key='topupCard'
						fullSizeButtons={isMobile}
						onRequest={async () => {
							const response = await GD.REQ_CARD_TOPUP_TEMPLATE.request()
							if (response.error || !response.data) {
								throw new Error(getFriendlyErrorMessage(response.error, "Could not load top-up options. Close this form and try again."))
							}
							const template = response.data
							if (template) {
								template.card_uid = card.uid
								template.currency = topUpCurrency
								const editable = template as unknown as Record<string, unknown>
								editable.$__amount_type = "amount"
								// Match CardEditForms' convention: start blank rather than "0", so
								// formatAmount doesn't pad it to "0.00" before the user types anything.
								if (!template.amount) editable.amount = ""
							}
							return template
						}}
						onChange={(data: ICardTopUp) => setTopUpAmount(Number(data?.amount))}
						onSubmit={async (data) => {
							const parsedAmount = parsePositiveAmountToMinorUnits(data.amount)
							if (typeof parsedAmount.amount !== "number")
								return amountValidationError(parsedAmount.error || "Enter a valid amount.")
							if (maxTopUp !== undefined && parsedAmount.amount > maxTopUp)
								return amountValidationError(
									`Not enough funds. You can add up to ${formatCurrencyWithNumberToSymbolWithNumber(maxTopUp, topUpCurrency)}, the ${topUpFeeRule} top-up fee comes from the same balance.`
								)
							const payload = {
								...data,
								amount: parsedAmount.amount
							}
							const previousBalance = getCardBalanceMinorUnits(card)
							setBalanceSyncing(true)
							const res = await GD.REQ_CARD_TOPUP.request(payload)
							if (!res.error) {
								setCardOperationNotice({
									phase: "pending",
									message: "Top up completed. Updating card balance..."
								})
								setActiveView("details")
								if (profile?.company_role === USER_ROLE.BUYER) {
									void GD.REQ_PROFILE.request().then(nextProfile => {
										if (!disposed.current && nextProfile) setProfile(nextProfile)
									})
								} else if (user?.company_uid) {
									void updateCompany(user.company_uid)
								}
								void refreshCardBalanceAfterTopUp(previousBalance)
							} else {
								setBalanceSyncing(false)
								if (/(does|do) not have enough balance/i.test(`${res.error.message} ${res.error.details ?? ""}`))
									return amountValidationError("Not enough funds to cover this amount and the top-up fee. Enter a smaller amount.")
							}
							return res
						}}
						primaryButton={{ title: "Top Up", disabled: !(topUpAmount > 0) }}
						secondaryButton={{ title: "Cancel", onClick: () => setActiveView("details") }}
					/>
					</Section>
				</CardSubView>
			</Box>
		)
	}

	if (activeView === "3ds") {
		return <Card3dsCodes cardUid={card.uid} card={card} onBack={() => setActiveView("details")} />
	}

	if (activeView === "edit" && canEditCard) {
		return <CardEditForms
			card={card}
			onBack={() => setActiveView("details")}
			onSaved={handleCardSaved}
		/>
	}

	if (activeView === "tx-control") {
		return (
			<Box data-dialog-layout="amount-form" sx={{ minWidth: 0 }}>
				<CardSubView
					title='Transaction control'
					description='Choose which merchants can charge this card and when suspicious activity freezes it.'
					card={card}
					masked={maskedNumber}
				>
					<Section title='Merchant rules'>
				<FormParser
					key='txControlCard'
					onRequest={async () => ({
						allowed_descriptors: card.allowed_descriptors || "",
						freeze_threshold: card.suspicious_freeze_threshold ?? 3,
						$__allowed_descriptors_label: "Allowed merchants",
						$__freeze_threshold_label: "Freeze threshold",
						$__freeze_threshold_type: "number"
					})}
					onSubmit={async (data) => {
						const freezeThreshold = Number(data.freeze_threshold)
						if (!Number.isInteger(freezeThreshold) || freezeThreshold < 1) {
							return {
								data,
								error: {
									code: 0,
									message: "Freeze threshold must be an integer greater than or equal to 1."
								}
							}
						}

						const res = await GD.REQ_CARD_TX_CONTROL.request({
							card_uid: card.uid,
							allowed_descriptors: String(data.allowed_descriptors || "").trim(),
							freeze_threshold: freezeThreshold
						})
						if (res.data && !res.error) {
							setActiveView("details")
							updateCardDetails({ uid: card.uid, card_holder_uid: card.card_holder_uid })
							return {}
						}
						return res
					}}
					primaryButton={{ title: "Save" }}
					secondaryButton={{ title: "Cancel", onClick: () => setActiveView("details") }}
				/>
					</Section>
				</CardSubView>
			</Box>
		)
	}

	if (activeView === "refund") {
		const defaultCurrency = card.currency || DEFAULT_CURRENCY
		const maxWithdrawableCents = getMaxWithdrawableCents(card)
		const maxAmountDisplay = formatCardAmountInput(maxWithdrawableCents)
		const reserveDisplay = formatCurrencyWithNumberToSymbolWithNumber(
			card.withdraw_residual_cents ?? FALLBACK_MIN_RESERVE_CENTS, defaultCurrency
		)

		return (
			<Box data-dialog-layout="amount-form" sx={{ minWidth: 0 }}>
				<CardSubView
					title='Withdraw from card'
					description='Return funds from this card to your available balance.'
					card={card}
					masked={maskedNumber}
					stats={[
						{ label: "Available for withdrawal", value: formatCurrencyWithNumberToSymbolWithNumber(maxWithdrawableCents, defaultCurrency) },
						{
							label: "Card balance",
							value: cardBalanceLoading ? <BalanceLoadingIndicator /> : formatCurrencyWithNumberToSymbolWithNumber(getCardBalanceMinorUnits(card) || 0, defaultCurrency)
						}
					]}
				>
					<Section title='Amount to withdraw'>
						<VBox gap='14px' sx={{ padding: "6px 0 4px" }}>
					<HBox gap='10px' sx={{ alignItems: "flex-end" }}>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<Input
								label={`Amount (Max: ${formatCurrencyWithNumberToSymbolWithNumber(maxWithdrawableCents, defaultCurrency)})`}
								name='amount'
								data={{ amount: refundAmount }}
								onChange={(value: string) => setRefundAmount(String(value ?? ""))}
								type='number'
							/>
						</Box>
						<Button
							title='MAX'
							variant='secondary'
							onClick={() => setRefundAmount(maxAmountDisplay)}
							disabled={maxWithdrawableCents <= 0}
							sx={{ minWidth: "72px", height: "44px", padding: "0 14px" }}
						/>
					</HBox>
					<Typo variant='caption' sx={{ color: "#64748B", fontSize: "0.8em" }}>
						{reserveDisplay} remains on the card (issuer requirement)
					</Typo>
						</VBox>
					<HBox
						gap='10px'
						sx={{
							...FORM_ACTIONS_SX,
							"@media (max-width: 600px)": {
								width: "100%"
							}
						}}
					>
						<Button
							title='Cancel'
							variant='secondary'
							onClick={() => setActiveView("details")}
							sx={{ "@media (max-width: 600px)": { flex: "1 1 0", minWidth: 0 } }}
						/>
						<Button
							title='Withdraw'
							variant='primary'
							disabled={withdrawBusy || maxWithdrawableCents <= 0}
							sx={{ "@media (max-width: 600px)": { flex: "1 1 0", minWidth: 0 } }}
							onClick={async () => {
								if (withdrawBusy) return
								const parsed = parsePositiveAmountToMinorUnits(refundAmount)
								if (parsed.error) {
									toast.error(parsed.error)
									return
								}
								const amountInCents = parsed.amount!
								if (amountInCents > maxWithdrawableCents) {
									toast.error(
										`You cannot withdraw this amount. ${reserveDisplay} must stay on the card (issuer requirement).`
									)
									return
								}
								const previousBalance = getCardBalanceMinorUnits(card)
								const payload = {
									card_uid: card.uid,
									amount: amountInCents,
									currency: defaultCurrency
								}
								setWithdrawBusy(true)
								setCardOperationNotice({
									phase: "pending",
									message: "Preparing withdrawal..."
								})
								const baseline = await GD.REQ_CARD_WITHDRAW_LIST.request({
									card_uid: card.uid
								})
								if (disposed.current) return
								if (baseline.error) {
									setWithdrawBusy(false)
									setBalanceSyncing(false)
									const message = getFriendlyErrorMessage(
										baseline.error,
										"Failed to prepare withdrawal"
									)
									setCardOperationNotice({ phase: "error", message })
									toast.error(message)
									return
								}
								const existingIds = new Set(baseline.data.map(item => item.id))
								setBalanceSyncing(true)
								const res = await GD.REQ_CARD_WITHDRAW.request(payload)
								if (disposed.current) return
								if (!res.error) {
									setCardOperationNotice({
										phase: "pending",
										message: "Withdrawal initiated. Checking status..."
									})
									toast.info("Withdrawal initiated. Checking status...")

									const poll = async (attempt = 0) => {
										if (attempt >= CARD_OPERATION_MAX_POLL_ATTEMPTS) {
											setWithdrawBusy(false)
											setBalanceSyncing(false)
											const message =
												"The withdrawal may still be running, but its final status was not confirmed."
											setCardOperationNotice({ phase: "warning", message })
											toast.warn(message)
											return
										}
										const listRes = await GD.REQ_CARD_WITHDRAW_LIST.request({
											card_uid: card.uid
										})
										if (disposed.current) return
										if (listRes.error) {
											setCardOperationNotice({
												phase: "warning",
												message: "Withdrawal status is temporarily unavailable. Retrying..."
											})
											schedulePoll(() => void poll(attempt + 1), CARD_WITHDRAW_POLL_INTERVAL_MS)
											return
										}
										const outcome = getNewWithdrawalOutcome(listRes.data, existingIds, {
											cardUid: card.uid,
											amount: amountInCents,
											currency: defaultCurrency
										})
										if (outcome.state === "waiting") {
											setCardOperationNotice({
												phase: "pending",
												message: "Withdrawal is still being processed..."
											})
											schedulePoll(() => void poll(attempt + 1), CARD_WITHDRAW_POLL_INTERVAL_MS)
											return
										}
										setWithdrawBusy(false)
										if (outcome.state === "failed") {
											setBalanceSyncing(false)
											const message = `Withdrawal failed (${outcome.withdrawal.status}).`
											setCardOperationNotice({ phase: "error", message })
											toast.error(message)
											return
										}
										setCardOperationNotice({ phase: "pending", message: "Withdrawal processed. Updating balances..." })
										void refreshCardBalanceAfterWithdrawal(previousBalance)
									}

									void poll()
									setActiveView("details")
								} else {
									setWithdrawBusy(false)
									setBalanceSyncing(false)
									const message = getFriendlyErrorMessage(res.error, "Failed to withdraw")
									setCardOperationNotice({ phase: "error", message })
									toast.error(message)
								}
							}}
						/>
					</HBox>
					</Section>
				</CardSubView>
			</Box>
		)
	}

	const createdDate = card.created_at ? new Date(card.created_at) : null
	const hasCreatedDate = Boolean(createdDate) && !Number.isNaN(createdDate!.getTime())
	const blockedDate = card.blocked_at ? new Date(card.blocked_at) : null
	const hasBlockedDate = Boolean(blockedDate) && !Number.isNaN(blockedDate!.getTime())
	const headerMeta = [
		cardIssuer !== null ? getIssuerDisplayName({ issuer: cardIssuer, issuer_label: card.issuer_label }) : null,
		card.currency,
		hasCreatedDate ? `Created ${createdDate!.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : null
	].filter(Boolean).join(" · ")
	const limitsLocked = !canEditCard || !areActionsReady || actionBusy || isCardTerminal || !canEditCardStatus
	const limitValue = (text: string, edit: string) =>
		isLoading
			? <Skeleton busy width='64px' height='20px' />
			: <LimitEditButton label={edit} disabled={limitsLocked} onClick={handleEdit}>{text}</LimitEditButton>
	const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" } as const
	const canChangeBalance = areActionsReady && isCardActive
	const balanceBusy = actionBusy || cardBalanceSyncing || withdrawBusy

	return (
		<VBox data-card-details gap='16px' sx={{ width: "100%", padding: "6px 8px 18px", "@media (max-width: 520px)": { padding: "4px 2px 14px" } }}>
			{onBack && (
				<Button
					title='Back'
					ariaLabel='Back to previous dialog'
					variant='secondarySmall'
					onClick={onBack}
					sx={{ alignSelf: "flex-start" }}
					icon={
						<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
							<path d='m15 18-6-6 6-6' />
						</svg>
					}
				/>
			)}

			<EntityHeader
				stacked
				leading={<CardGlyph network={card.network} size={56} />}
				title={card.name || "Virtual card"}
				meta={headerMeta}
				badge={!isLoading && <CardStatusPill status={card.status} />}
			/>

			<Stage>
				<Box sx={{ minWidth: 0, width: "100%", maxWidth: "400px", justifySelf: "center" }}>{CardVisual()}</Box>
				<BalanceHero
					loading={cardBalanceLoading}
					amount={typeof cardBalance === "number" ? formatCurrencyWithNumberToSymbolWithNumber(cardBalance, card.currency || "USD") : "N/A"}
					hint={!isLoading && (card.balance_threshold || 0) > 0 ? `Auto top-up below ${formatCurrencyWithNumberToSymbolWithNumber(card.balance_threshold || 0, card.currency || "USD")}` : undefined}
					actions={canChangeBalance ? (
						<>
							<HeroActionButton onClick={handleTopUp} disabled={balanceBusy}>
								<svg aria-hidden width='16' height='16' viewBox='0 0 24 24' {...stroke}><path d='M12 5v14' /><path d='M5 12h14' /></svg>
								Top Up
							</HeroActionButton>
							{canRefund && (
								<HeroActionButton ghost onClick={handleRefund} disabled={balanceBusy}>
									<svg aria-hidden width='16' height='16' viewBox='0 0 24 24' {...stroke}><path d='M5 12h14' /></svg>
									Withdraw
								</HeroActionButton>
							)}
						</>
					) : undefined}
					footer={
						<Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px" }}>
							<StageLimit label='Monthly limit'>{limitValue(formatCardLimit(card.limit_monthly, card.currency), "Edit monthly limit")}</StageLimit>
							<StageLimit label='Daily limit'>{limitValue(formatCardLimit(card.limit_daily, card.currency), "Edit daily limit")}</StageLimit>
							<StageLimit label='Auto top-up at'>
								{isLoading ? <Skeleton busy width='64px' height='20px' /> : formatCurrencyWithNumberToSymbolWithNumber(card.balance_threshold || 0, card.currency || "USD")}
							</StageLimit>
						</Box>
					}
				/>
			</Stage>

			{areActionsReady ? (
				<QuickActions label='Card tools'>
					<ToolTile
						title='3DS codes'
						onClick={handleOpen3ds}
						disabled={isCreating || isCardTerminal}
						icon={<svg width='18' height='18' viewBox='0 0 24 24' {...stroke}><rect x='3' y='5' width='18' height='14' rx='2' /><path d='M7 10h.01M12 10h.01M17 10h.01M8 15h8' /></svg>}
					/>
					<ToolTile
						title={isCreating ? "Transactions after activation" : "Transactions"}
						onClick={() => {
							if (onTransactions) onTransactions(cardUid)
							else router.navigate("transactions", undefined, { cardId: cardUid })
							onClose()
						}}
						disabled={isCreating}
						icon={<svg width='18' height='18' viewBox='0 0 24 24' {...stroke}><path d='M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01' /></svg>}
					/>
					{showEditCard && canEditCard && (
						<ToolTile
							title='Edit card'
							onClick={handleEdit}
							disabled={actionBusy || isCardTerminal || !canEditCardStatus}
							icon={<svg width='18' height='18' viewBox='0 0 24 24' {...stroke}><path d='M12 20h9' /><path d='M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z' /></svg>}
						/>
					)}
					{canManageTxControl && (
						<ToolTile
							title='Tx control'
							onClick={handleTxControl}
							disabled={actionBusy || isCardTerminal || !isCardActive}
							icon={<svg width='18' height='18' viewBox='0 0 24 24' {...stroke}><path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' /><path d='M9 12l2 2 4-4' /></svg>}
						/>
					)}
				</QuickActions>
			) : (
				<Skeleton busy width='100%' height='96px' sx={{ borderRadius: DS.radius }} />
			)}

			<Section title='Card information'>
				<Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", columnGap: "32px", [DS.mobile]: { gridTemplateColumns: "minmax(0, 1fr)" } }}>
					<DetailRow label='Card ID' value={isLoading ? <Skeleton busy width='140px' height='18px' /> : card.uid} mono copy={isLoading ? undefined : card.uid} />
					{hasCreatedDate && (
						<DetailRow
							label='Created'
							value={isLoading ? <Skeleton busy width='120px' height='18px' /> : createdDate!.toLocaleString("en-GB", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
						/>
					)}
					{!isLoading && hasBlockedDate && (
						<DetailRow
							label='Blocked'
							value={blockedDate!.toLocaleString("en-GB", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
						/>
					)}
					{company?.data && !isPrivateAccount && (
						<DetailRow label='Company' value={isLoading ? <Skeleton busy width='100px' height='18px' /> : company.data.name} />
					)}
					{card.bin && <DetailRow label='BIN / Country' value={card.bin + (card.country_code ? " · " + card.country_code : "")} />}
				</Box>
			</Section>

			<div data-card-actions>
				<Section
					title='Security'
					action={
						<ActionButton
							onClick={handleRefreshCardStatus}
							disabled={statusRefreshBusy || isLoading || withdrawBusy || cardBalanceSyncing || (actionBusy && !isBlockPending)}
						>
							<span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
								<svg aria-hidden width='14' height='14' viewBox='0 0 24 24' {...stroke} style={{ animation: statusRefreshBusy ? "spin 1s linear infinite" : "none" }}>
									<path d='M21 12a9 9 0 1 1-3-6.7' /><path d='M21 4v5h-5' />
								</svg>
								{statusRefreshBusy ? "Refreshing..." : "Refresh status"}
							</span>
						</ActionButton>
					}
				>
					<VBox gap='12px' sx={{ padding: "4px 0 12px" }}>
				{!isLoading && freezeReasonLabel && (
					<Box
						sx={{
							padding: "12px 16px",
							borderRadius: "12px",
							backgroundColor: "#FFFBEB",
							border: "1px solid #FDE68A",
							borderLeft: "4px solid #F59E0B"
						}}
					>
						<Typo
							variant='caption'
							sx={{
								color: "#92400E",
								fontWeight: 700
							}}
						>
							Freeze context
						</Typo>
						<Typo sx={{ color: "#92400E", marginTop: "4px", lineHeight: 1.5 }}>
							{freezeReasonLabel}
						</Typo>
						{card.frozen_at && (
							<Typo variant='caption' sx={{ color: "#B45309", marginTop: "6px" }}>
								Since {new Date(card.frozen_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
							</Typo>
						)}
					</Box>
				)}
				{!isLoading && isCreating && (
					<VBox
						gap='10px'
						sx={{
							padding: "14px 16px",
							borderRadius: "12px",
							backgroundColor: "#FFFBEB",
							border: "1px solid #FDE68A",
							borderLeft: "4px solid #F59E0B"
						}}
					>
						<Typo sx={{ color: "#92400E", fontWeight: 800 }}>
							Card creation in progress
						</Typo>
						<Typo sx={{ color: "#92400E", lineHeight: 1.5 }}>
							The issuance fee shown during confirmation has been charged from
							{isPrivateAccount ? " your balance" : " the company balance"} and is not part of this card&apos;s transaction history. This
							card is not active yet; sensitive details, card transactions and transaction
							controls will become available after activation. You can safely close this
							window.
						</Typo>
					</VBox>
				)}
				{isLoading && (
					<VBox
						role='status'
						aria-label='Loading sensitive card details'
						gap='8px'
						sx={{
							padding: "14px 16px",
							borderRadius: "12px",
							backgroundColor: "#F8FAFC",
							border: "1px solid #E2E8F0"
						}}
					>
						<Skeleton busy width='58%' height='14px' />
						<Skeleton busy width='82%' height='12px' />
					</VBox>
				)}
				{cardDetailsFailed && (
					<Box
						role='alert'
						sx={{
							padding: "12px 16px",
							borderRadius: "12px",
							backgroundColor: "#FFF7ED",
							border: "1px solid #FED7AA",
							borderLeft: "4px solid #EA580C"
						}}
					>
						<Typo sx={{ color: "#9A3412", lineHeight: 1.5 }}>
							Card details could not be loaded.
						</Typo>
					</Box>
				)}
				{!isLoading && !cardDetailsFailed && !hasVisibleCardInfo && !isCreating && (
					<Box
						sx={{
							padding: "12px 16px",
							borderRadius: "12px",
							backgroundColor: "#F8FAFC",
							border: "1px solid #E2E8F0"
						}}
					>
						<Typo sx={{ color: "#64748B", lineHeight: 1.5 }}>
							Sensitive card fields are unavailable for this card status.
						</Typo>
					</Box>
				)}
				{cardOperationNotice && (
					<Box
						role='status'
						aria-live={cardOperationNotice.phase === "pending" ? "polite" : "assertive"}
						sx={{
							padding: "12px 16px",
							borderRadius: "12px",
							backgroundColor:
								CARD_OPERATION_NOTICE_COLORS[cardOperationNotice.phase].background,
							border: `1px solid ${CARD_OPERATION_NOTICE_COLORS[cardOperationNotice.phase].border}`
						}}
					>
						<Typo
							sx={{
								color: CARD_OPERATION_NOTICE_COLORS[cardOperationNotice.phase].text,
								fontSize: "0.84rem",
								fontWeight: 700,
								lineHeight: 1.5
							}}
						>
							{cardOperationNotice.message}
						</Typo>
					</Box>
				)}

				{showBlockConfirmation && (
					<VBox
						gap='12px'
						sx={{
							padding: "16px",
							borderRadius: "14px",
							backgroundColor: "#FEF2F2",
							border: "1px solid #FECACA"
						}}
					>
						{blockPreviewLoading ? <BalanceLoadingIndicator label='Loading return estimate' /> : blockPreviewError ? (
							<Typo sx={{ color: "#B91C1C", fontSize: "0.84rem", lineHeight: 1.5 }}>{blockPreviewError}</Typo>
						) : blockPreview && (
							<VBox gap='6px'>
								<Typo sx={{ color: "#4B5563", fontSize: "0.84rem" }}>Balance at issuer: {formatCurrencyWithNumberToSymbolWithNumber(blockPreview.balance, blockPreview.currency)}</Typo>
								{blockPreview.residual > 0 && <Typo sx={{ color: "#4B5563", fontSize: "0.84rem" }}>{blockPreview.issuer_label} keeps: −{formatCurrencyWithNumberToSymbolWithNumber(blockPreview.residual, blockPreview.currency)}</Typo>}
								{blockPreview.blockable ? <Typo sx={{ color: "#1F2937", fontWeight: 800, fontSize: "0.9rem", borderTop: "1px solid #CBD5E1", paddingTop: "7px" }}>Returned to {isPrivateAccount ? "your balance" : blockPreview.destination === "buyer" ? "the buyer balance" : "the company"}: {formatCurrencyWithNumberToSymbolWithNumber(blockPreview.returnable, blockPreview.currency)}</Typo> : <Typo sx={{ color: "#B91C1C", fontSize: "0.84rem" }}>{blockPreview.blocked_reason || "This card cannot be closed right now."}</Typo>}
								{blockPreview.warnings.map((warning, index) => <Typo key={index} sx={{ color: "#64748B", fontSize: "0.76rem" }}>{warning}</Typo>)}
							</VBox>
						)}
						<Typo sx={{ color: "#4B5563", fontSize: "0.84rem", lineHeight: 1.5 }}>Blocking is irreversible.</Typo>
						<HBox gap='10px' sx={{ width: "100%", flexWrap: "wrap" }}>
							<Button
								title='Confirm Block'
								disabled={actionBusy || blockPreviewLoading || !blockPreview?.blockable}
								onClick={() => {
									void requestCardBlock()
								}}
							/>
							<Button
								title='Cancel'
								variant='secondary'
								disabled={actionBusy}
								onClick={() => setShowBlockConfirmation(false)}
							/>
						</HBox>
					</VBox>
				)}

						{areActionsReady ? (
							(isCardActive || isCardFrozen) && (
								<VBox gap='0px' sx={{ borderRadius: "14px", border: `1px solid ${DS.line}`, overflow: "hidden", "& > * + *": { borderTop: `1px solid ${DS.line}` } }}>
									{isCardActive && (
										<SecurityRow
											tone='warning'
											title='Freeze card'
											description='Pause all payments. You can unfreeze it any time.'
											icon={<svg width='18' height='18' viewBox='0 0 24 24' {...stroke}><path d='M12 2v20M4.9 4.9l14.2 14.2M2 12h20M4.9 19.1 19.1 4.9' /></svg>}
										>
											<ControlButton tone='warning' onClick={handleFreeze} disabled={actionBusy || !canFreezeCard}>
												{isFreezePending ? "Freezing..." : "Freeze Card"}
											</ControlButton>
										</SecurityRow>
									)}
									{isCardFrozen && canUnfreezeCard && (
										<SecurityRow
											tone='success'
											title='Card is frozen'
											description='Payments are paused. Unfreeze to use the card again.'
											icon={<svg width='18' height='18' viewBox='0 0 24 24' {...stroke}><rect x='3' y='11' width='18' height='11' rx='2' /><path d='M7 11V7a5 5 0 0 1 9.9-1' /></svg>}
										>
											<ControlButton tone='success' onClick={handleUnfreeze} disabled={actionBusy}>
												{isUnfreezePending ? "Unfreezing..." : "Unfreeze Card"}
											</ControlButton>
										</SecurityRow>
									)}
									<SecurityRow
										tone='danger'
										title='Block card'
										description='Permanently closes the card. The remaining balance is returned.'
										icon={<svg width='18' height='18' viewBox='0 0 24 24' {...stroke}><circle cx='12' cy='12' r='9' /><path d='M5.6 5.6l12.8 12.8' /></svg>}
									>
										<ControlButton tone='danger' onClick={handleBlock} disabled={actionBusy || !canBlockCard}>
											{!canBlockCard ? "Block unavailable" : isBlockPending ? "Blocking..." : "Block Card"}
										</ControlButton>
									</SecurityRow>
								</VBox>
							)
						) : (
							<Skeleton busy width='100%' height='44px' sx={{ borderRadius: "12px" }} />
						)}
					</VBox>
				</Section>
			</div>

			{chartData.length > 0 && (
				<Section title='Spending activity'>
					<Box sx={{ height: "220px", width: "100%", padding: "8px 0 12px" }}>
						<PositiveSizeChart>
							<AreaChart
								data={chartData}
								margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
							>
								<defs>
									<linearGradient id='colorSpend' x1='0' y1='0' x2='0' y2='1'>
										<stop offset='5%' stopColor='#2B5C8A' stopOpacity={0.25} />
										<stop offset='95%' stopColor='#2B5C8A' stopOpacity={0.0} />
									</linearGradient>
								</defs>
								<CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#f3f4f6' />
								<XAxis
									dataKey='date'
									axisLine={false}
									tickLine={false}
									tick={{ fill: "#9ca3af", fontSize: 10, fontWeight: 500 }}
									dy={12}
									minTickGap={10}
								/>
								<YAxis
									axisLine={false}
									tickLine={false}
									tick={{ fill: "#9ca3af", fontSize: 10, fontWeight: 500 }}
									tickFormatter={(val) =>
										formatCurrencyWithNumberToSymbolWithNumber(
											val,
											card.currency || DEFAULT_CURRENCY
										)
									}
								/>
								<Tooltip
									contentStyle={{
										borderRadius: "10px",
										backgroundColor: "rgba(17, 24, 39, 0.95)",
										backdropFilter: "blur(6px)",
										border: "1px solid rgba(255,255,255,0.1)",
										boxShadow:
											"0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
										fontSize: "12px",
										color: "#fff",
										padding: "8px 12px"
									}}
									itemStyle={{ color: "#34d399", fontWeight: 600 }}
									labelStyle={{ color: "#9ca3af", marginBottom: "4px", fontWeight: 500 }}
									formatter={(
										value: string | number | Array<string | number> | undefined
									) => [
										formatCurrencyWithNumberToSymbolWithNumber(
											Number(value || 0),
											card.currency || DEFAULT_CURRENCY
										),
										"Spent"
									]}
									labelFormatter={(_label, payload) =>
										payload?.[0]?.payload?.fullDate || _label
									}
								/>
								<Area
									type='monotone'
									dataKey='amount'
									stroke='#2B5C8A'
									strokeWidth={2.5}
									fillOpacity={1}
									fill='url(#colorSpend)'
									activeDot={{ r: 5, stroke: "#fff", strokeWidth: 1.5, fill: "#2B5C8A" }}
								/>
							</AreaChart>
						</PositiveSizeChart>
					</Box>
				</Section>
			)}
		</VBox>
	)
}
